import { useEffect, useState, useSyncExternalStore } from 'react';
import type { RtStatus } from '../../collaboration/rt/connection';
import type { LiveSession } from '../../collaboration/rt/liveSession';
import { t } from '../../i18n/I18n';
import { Alert } from '../Alert';
import { showsAccessNotice } from '../EditorView';
import { useStore } from '../useStore';

export type InviteFailure = 'invalid' | 'unreachable' | 'unreadable' | 'closed' | 'superseded' | 'full';
const noLive = () => () => {};
/** A link back within this long is never mentioned. */
const RECONNECT_NOTICE_MS = 2000;

/** A dropped link or a failed try is trouble; a rejoin the web asked for is not, nor a try under way the person did not see start. */
export const linkTrouble = (status: RtStatus, noticed: boolean): boolean => status === 'reconnecting' || (noticed && status === 'connecting');
/** Back live or ended, the connection alert starts over: a later try (Refresh after 4008) is no trouble already shown. */
export const noticeStartsOver = (status: RtStatus | undefined, ended: boolean): boolean => status === 'live' || ended;

/**
 * The alerts of a project opened by an invitation, over the editor: the opening card, an end the person must leave on or
 * may try again from, the link down, edit rights changed — the app's MultiplayerLoadingPopup,
 * MultiplayerConnectionLostPopup and MultiplayerPermissionsChangedPopup.
 */
export function SessionAlerts({ live, failure, onLeave, onRetryInvite }: {
  live: LiveSession | null; failure: InviteFailure | null; onLeave: () => void; onRetryInvite: () => void;
}) {
  const { opening, projectName } = useStore(s => ({ opening: s.loading || !s.scene, projectName: s.projectName }));
  const state = useSyncExternalStore(live?.subscribe ?? noLive, () => live?.get() ?? null);
  const [noticed, setNoticed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const down = !!state && !opening && !state.terminal && linkTrouble(state.status, noticed);
  useEffect(() => {
    if (noticeStartsOver(state?.status, !!state?.terminal)) { setNoticed(false); setDismissed(false); }
    if (!down || noticed) return;
    const timer = setTimeout(() => setNoticed(true), RECONNECT_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [down, noticed, state?.status, state?.terminal]);

  const leave = { label: t('Ok'), onSelect: onLeave };
  const back = { label: t('#AppBackToProjects'), onSelect: onLeave };
  const retry = { label: t('#TryAgainButton'), onSelect: () => live?.retry(), busy: state?.status === 'connecting' };
  const lost = { title: t('#InternetConnectionUnstable'), text: t('#ProjectWillRefreshOnReconnect') };
  if (failure === 'full') return <Alert key="full" title={t('#RoomIsFullTitle')} text={t('#RoomIsFullDesc')} primary={leave} onClose={onLeave} />;
  if (failure === 'closed' || failure === 'invalid') return <Alert key="unavailable" title={t('#LinkUnavailableTitle')} text={t('#LinkUnavailable')} primary={leave} onClose={onLeave} />;
  // The tries after a join ran out: closing leaves, as the app's Close does, and a try from here shows its progress.
  if (failure === 'unreachable' && !opening) return <Alert key="connection-failed" {...lost} primary={{ ...retry, onSelect: () => { setNoticed(true); setDismissed(false); live?.retry(); } }}
    secondary={{ label: t('Close'), onSelect: onLeave }} onClose={onLeave} />;
  if (failure === 'superseded') return <Alert key="superseded" title={projectName || t('#AppProjectFallbackName')} text={t('#WebSessionOpenedElsewhere')}
    primary={{ label: t('#CollabRefreshConnection'), onSelect: () => live?.retry() }} secondary={back} onClose={onLeave} />;
  if (failure === 'unreachable') return <Alert key="unreachable" title={t('#AppOpenFailedTitle')} text={t('#CollabErrorConnect')}
    primary={{ label: t('#CollabRetryCheck'), onSelect: onRetryInvite }} secondary={back} onClose={onLeave} />;
  if (failure === 'unreadable') return <Alert key="unreadable" title={t('#AppOpenFailedTitle')} text={t('#InviteProjectUnreadable')} primary={back} onClose={onLeave} />;
  if (opening) return <Alert key="opening" title={t('#OpeningSharedProject')} text={t('#LoadingText')} spinner secondary={{ label: t('Cancel'), onSelect: onLeave }} onClose={onLeave} />;
  if (state && down && noticed && !dismissed) return <Alert key="connection" {...lost} primary={retry}
    secondary={{ label: t('Close'), onSelect: () => setDismissed(true) }} onClose={() => setDismissed(true)} />;
  const roleChange = state?.roleChange === 'edit' && !showsAccessNotice(true, state.idless) ? null : state?.roleChange;
  if (live && roleChange) return <Alert key="permissions" title={t('#PermissionsChanged')} text={t(roleChange === 'view' ? '#YourAccessChangedToView' : '#YourAccessChangedToEdit')}
    primary={{ label: t('Continue'), onSelect: () => live.acknowledgeRole() }} onClose={() => live.acknowledgeRole()} />;
  return null;
}
